import { useState, useEffect, useCallback } from "react";

const signoffDefault = {
  prep_so: [] as Array<{ name: string; metadata: string }>,
  rev_so: [] as Array<{ name: string; metadata: string }>,
  conc_so: [] as Array<{ name: string; metadata: string }>,
  eqr_so: [] as Array<{ name: string; metadata: string }>,
  partner_so: [] as Array<{ name: string; metadata: string }>,
};

export interface WorkspaceMisstatement {
  id: string;
  workspace: string;
  type: "Factual" | "Judgemental" | "Projected";
  data: any;
  signoff: {
    prep_so: Array<{ name: string; metadata: string }>;
    rev_so: Array<{ name: string; metadata: string }>;
    conc_so: Array<{ name: string; metadata: string }>;
    eqr_so: Array<{ name: string; metadata: string }>;
    partner_so: Array<{ name: string; metadata: string }>;
  };
}

export function useWorkspaceMisstatements(workspaceName: string) {
  const [workspaceMisstatements, setWorkspaceMisstatements] = useState<
    WorkspaceMisstatement[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const loadWorkspaceMisstatements = useCallback(async () => {
    if (!workspaceName) {
      setWorkspaceMisstatements([]);
      return;
    }
    try {
      setLoading(true);
      setError("");

      const sp: any = (window as any).sharePointAPI;
      if (!sp?.downloadMisstatementJson) {
        setWorkspaceMisstatements([]);
        return;
      }

      const misRes = await sp.downloadMisstatementJson();

      if (!misRes?.success || !misRes.data) {
        setWorkspaceMisstatements([]);
        return;
      }

      const misstatementData = misRes.data;
      const librariesMisstatement =
        misstatementData.Libraries_Misstatement ||
        misstatementData.libraries_Misstatement ||
        {};

      const misstatements = librariesMisstatement.misstatements || {};

      const filtered: WorkspaceMisstatement[] = Object.entries(
        misstatements as Record<string, any>,
      )
        .map(([id, value]: [string, any]) => {
          const signoff = value.signoff || {};
          return {
            id,
            workspace: value.workspace || "",
            type: value.type || "Factual",
            data: value,
            signoff: {
              prep_so: Array.isArray(signoff.prep_so) ? signoff.prep_so : signoffDefault.prep_so,
              rev_so: Array.isArray(signoff.rev_so) ? signoff.rev_so : signoffDefault.rev_so,
              conc_so: Array.isArray(signoff.conc_so) ? signoff.conc_so : signoffDefault.conc_so,
              eqr_so: Array.isArray(signoff.eqr_so) ? signoff.eqr_so : signoffDefault.eqr_so,
              partner_so: Array.isArray(signoff.partner_so) ? signoff.partner_so : signoffDefault.partner_so,
            },
          };
        })
        .filter((mis) => mis.workspace === workspaceName);

      setWorkspaceMisstatements(filtered);
    } catch (e: any) {
      setError(
        e?.message || "Unexpected error while loading workspace misstatements",
      );
      setWorkspaceMisstatements([]);
    } finally {
      setLoading(false);
    }
  }, [workspaceName]);

  useEffect(() => {
    loadWorkspaceMisstatements();
  }, [loadWorkspaceMisstatements]);

  // Group misstatements by type
  const factual = workspaceMisstatements.filter((m) => m.type === "Factual");
  const judgemental = workspaceMisstatements.filter(
    (m) => m.type === "Judgemental",
  );
  const projected = workspaceMisstatements.filter(
    (m) => m.type === "Projected",
  );

  return {
    misstatements: workspaceMisstatements,
    factual,
    judgemental,
    projected,
    loading,
    error,
    hasMisstatements: workspaceMisstatements.length > 0,
    loadWorkspaceMisstatements,
  };
}

